import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useViewportScroll, useTransform } from 'framer-motion';
import { 
  FaChevronRight, FaMapMarkerAlt, FaGlobe, FaPhoneAlt, FaEnvelope,
  FaChevronLeft, FaHandHoldingHeart, FaCalendarAlt, FaGraduationCap,
  FaTrophy, FaUsers, FaChalkboardTeacher, FaBookOpen, FaFlask,
  FaChartLine, FaAward, FaIndustry, FaStar
} from 'react-icons/fa';

// Dummy data (replace with actual data later)
const instituteData = {
  name: "Indian Institute of Technology, Bombay",
  logo: "https://upload.wikimedia.org/wikipedia/en/thumb/1/1d/Indian_Institute_of_Technology_Bombay_Logo.svg/1200px-Indian_Institute_of_Technology_Bombay_Logo.svg.png",
  coverPhoto: "https://www.iitb.ac.in/sites/www.iitb.ac.in/files/styles/picture_slide_1345_600/public/field/slideshow/Mainbuilding.jpg?itok=Cfqh3YJn",
  location: "Mumbai, Maharashtra",
  website: "www.iitb.ac.in",
  phone: "+91-22-25767890",
  email: "info@iitb.ac.in",
  description: "IIT Bombay is a public technical and research university located in Powai, Mumbai. It is recognized worldwide as a leader in the field of engineering education and research.",
  stats: {
    founded: 1958,
    students: 10000,
    faculty: 650,
    researchCenters: 25,
    placement: "95%",
    internationalCollaborations: 200
  },
  rankings: [
    { name: "NIRF", rank: 3 },
    { name: "QS World", rank: 177 },
    { name: "THE World", rank: 401 }
  ],
  jobPosts: [
    { title: "Assistant Professor in Computer Science", deadline: "2024-08-30" },
    { title: "Research Associate in Nanotechnology", deadline: "2024-09-15" },
    { title: "PhD Position in Artificial Intelligence", deadline: "2024-10-01" }
  ],
  campaigns: [
    { title: "Green Campus Initiative", goal: "₹50 Lakhs", raised: "₹35 Lakhs" },
    { title: "Scholarship Fund for Underprivileged", goal: "₹1 Crore", raised: "₹80 Lakhs" }
  ],
  events: [
    { title: "Annual Tech Fest", date: "2024-12-27" },
    { title: "International Conference on AI", date: "2025-02-15" },
    { title: "Alumni Meet 2024", date: "2024-11-20" }
  ],
  successStories: [
    { name: "Dr. Raghuram Rajan", achievement: "Former Governor of RBI" },
    { name: "Nandan Nilekani", achievement: "Co-founder of Infosys" },
    { name: "Parag Agrawal", achievement: "Former CEO of Twitter" }
  ],
  gallery: [
    "https://www.iitb.ac.in/sites/www.iitb.ac.in/files/styles/picture_slide_1345_600/public/field/slideshow/Academic%20area.jpg?itok=jbBTHkpr",
    "https://www.iitb.ac.in/sites/www.iitb.ac.in/files/styles/picture_slide_1345_600/public/field/slideshow/Hostel%201%20and%204.jpg?itok=UbvnXcX3",
    "https://www.iitb.ac.in/sites/www.iitb.ac.in/files/styles/picture_slide_1345_600/public/field/slideshow/Lake%20side.jpg?itok=6tXfhnwP"
  ]
};

const ParallaxSection = ({ children, yOffset = 100 }) => {
  const { scrollY } = useViewportScroll();
  const y = useTransform(scrollY, [0, 1000], [0, -yOffset]);
  return (
    <motion.div style={{ y }}>{children}</motion.div>
  );
};

const AnimatedCounter = ({ value, duration = 2 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseInt(value.toString().replace(/,/g, ''));
    if (start === end) return;

    let totalMilSecDur = parseInt(duration * 1000);
    let incrementTime = (totalMilSecDur / end) * 1000;

    let timer = setInterval(() => {
      start += 1;
      setCount(String(start).replace(/\B(?=(\d{3})+(?!\d))/g, ","));
      if (start === end) clearInterval(timer);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{count}</span>;
};

const InstituteProfilePage = () => {
  const [currentImage, setCurrentImage] = useState(0);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % instituteData.gallery.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + instituteData.gallery.length) % instituteData.gallery.length);
  };

  return (
    <div className="bg-gradient-to-b from-gray-100 to-blue-50 min-h-screen">
      {/* Hero Section */}
      <ParallaxSection>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative h-screen bg-cover bg-center bg-fixed"
          style={{ backgroundImage: `url(${instituteData.coverPhoto})` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-center text-white">
              <motion.img 
                src={instituteData.logo} 
                alt={instituteData.name} 
                className="w-40 h-40 mx-auto mb-6 rounded-full bg-white p-2"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
              />
              <motion.h1 
                className="text-6xl font-bold mb-4"
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {instituteData.name}
              </motion.h1>
              <motion.p 
                className="text-2xl"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <FaMapMarkerAlt className="inline mr-2" />{instituteData.location}
              </motion.p>
            </div>
          </div>
        </motion.div>
      </ParallaxSection>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        {/* Institute Info */}
        <motion.div 
          className="bg-white rounded-2xl shadow-2xl p-8 mb-16"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-4xl font-bold mb-6 text-blue-800">About Us</h2>
          <p className="text-gray-700 mb-6 text-lg leading-relaxed">{instituteData.description}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: FaGlobe, text: instituteData.website, href: `https://${instituteData.website}` },
              { icon: FaPhoneAlt, text: instituteData.phone },
              { icon: FaEnvelope, text: instituteData.email, href: `mailto:${instituteData.email}` }
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="flex items-center justify-center bg-blue-50 rounded-lg p-4"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <item.icon className="text-3xl text-blue-500 mr-3" />
                {item.href ? (
                  <a href={item.href} className="text-blue-500 hover:underline">{item.text}</a>
                ) : (
                  <span>{item.text}</span>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Stats and Rankings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-16">
          <motion.div 
            className="bg-white rounded-2xl shadow-2xl p-8"
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-4xl font-bold mb-6 text-blue-800">Key Statistics</h2>
            <div className="grid grid-cols-2 gap-8">
              {Object.entries(instituteData.stats).map(([key, value], index) => (
                <motion.div 
                  key={key} 
                  className="text-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <p className="text-4xl font-bold text-blue-600 mb-2">
                    <AnimatedCounter value={value} />
                  </p>
                  <p className="text-gray-600 capitalize text-lg">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div 
            className="bg-white rounded-2xl shadow-2xl p-8"
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-4xl font-bold mb-6 text-blue-800">Rankings</h2>
            {instituteData.rankings.map((ranking, index) => (
              <motion.div 
                key={ranking.name}
                className="flex justify-between items-center mb-4 bg-blue-50 rounded-lg p-4"
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 * index }}
              >
                <span className="text-xl font-semibold">{ranking.name}</span>
                <span className="text-3xl font-bold text-blue-600">#{ranking.rank}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Job Posts, Campaigns, and Events */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 mb-16">
          {[
            { title: "Job Openings", icon: FaIndustry, data: instituteData.jobPosts, key: "title", subKey: "deadline" },
            { title: "Ongoing Campaigns", icon: FaHandHoldingHeart, data: instituteData.campaigns, key: "title", subKey: "goal" },
            { title: "Upcoming Events", icon: FaCalendarAlt, data: instituteData.events, key: "title", subKey: "date" }
          ].map((section, sectionIndex) => (
            <motion.div 
              key={section.title}
              className="bg-white rounded-2xl shadow-2xl p-8"
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 * sectionIndex }}
            >
              <h2 className="text-3xl font-bold mb-6 text-blue-800 flex items-center">
                <section.icon className="mr-3" />
                {section.title}
              </h2>
              {section.data.map((item, index) => (
                <motion.div 
                  key={index}
                  className="mb-4 p-4 bg-blue-50 rounded-lg"
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 * index }}
                  whileHover={{ scale: 1.03 }}
                >
                  <h3 className="font-semibold text-lg">{item[section.key]}</h3>
                  <p className="text-sm text-gray-600">{section.subKey}: {item[section.subKey]}</p>
                </motion.div>
              ))}
            </motion.div>
          ))}
        </div>

        {/* Success Stories */}
        <motion.div 
          className="bg-white rounded-2xl shadow-2xl p-8 mb-16"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <h2 className="text-4xl font-bold mb-8 text-blue-800 text-center">Success Stories</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {instituteData.successStories.map((story, index) => (
              <motion.div 
                key={index}
                className="bg-blue-50 rounded-lg p-6 text-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ scale: 1.05, boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)" }}
              >
                <FaGraduationCap className="text-6xl text-blue-500 mx-auto mb-4" />
                <h3 className="font-semibold text-xl mb-2">{story.name}</h3>
                <p className="text-gray-600">{story.achievement}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Image Gallery */}
        <motion.div 
          className="bg-white rounded-2xl shadow-2xl p-8 mb-16"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <h2 className="text-4xl font-bold mb-8 text-blue-800 text-center">Campus Glimpses</h2>
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.img 
                key={currentImage}
                src={instituteData.gallery[currentImage]} 
                alt={`Campus view ${currentImage + 1}`}
                className="w-full h-[600px] object-cover rounded-lg"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.5 }}
              />
            </AnimatePresence>
            <motion.button 
              onClick={prevImage} 
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white p-3 rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-200"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaChevronLeft className="text-gray-800 text-2xl" />
            </motion.button>
            <motion.button 
              onClick={nextImage} 
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white p-3 rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-200"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaChevronRight className="text-gray-800 text-2xl" />
            </motion.button>
          </div>
          <div className="flex justify-center mt-6">
            {instituteData.gallery.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`w-4 h-4 rounded-full mx-2 ${
                  currentImage === index ? 'bg-blue-500' : 'bg-gray-300'
                }`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.8 }}
              />
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
        className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-2xl shadow-2xl p-12 text-center"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <h2 className="text-5xl font-bold mb-6">Join Our Community</h2>
        <p className="text-2xl mb-8">Become a part of our prestigious institution and shape your future!</p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-white text-blue-600 px-12 py-4 rounded-full font-bold text-xl shadow-md hover:bg-gray-100 transition-colors duration-200"
        >
          Apply Now
        </motion.button>
      </motion.div>

    
    </div>
  </div>
  );
}
export default InstituteProfilePage;